/* eslint-disable react/no-array-index-key */

import Collapse from "@material-ui/core/Collapse";
import FormControl from "@material-ui/core/FormControl";
import FormGroup from "@material-ui/core/FormGroup";
import Masonry from "react-masonry-css";
import React, { FC, useMemo, useState } from "react";
import { ExpandLess, ExpandMore } from "@material-ui/icons";
import { Theme } from "@material-ui/core/styles";
import { styled } from "@material-ui/styles";
import { useSelector } from "react-redux";

import CheckBox from "../../../components/elements/CheckBox";
import { ReduxApplicationState } from "../../../types/ReduxTypes/ReduxApplicationState";
import { RoleSet, RoleTitleGroup } from "../../../types/campaignTypes";
import { SecondaryButton } from "../../../components/redesign/elements/Button";
import { getRoleKey, walkRoleSets } from "./roleTitleSetUtils";

const PickerRoot = styled("div")(({ theme }: { theme: Theme }) => ({
  background: theme.palette.brand.white,
  border: `1px solid ${theme.palette.brand.gray200}`,
  borderRadius: 10,
  overflow: "hidden",
}));

const Toolbar = styled("div")(({ theme }: { theme: Theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "12px 16px",
  borderBottom: `1px solid ${theme.palette.brand.gray100}`,
}));

const SearchInput = styled("input")(({ theme }: { theme: Theme }) => ({
  flex: 1,
  height: 36,
  padding: "0 12px",
  background: theme.palette.brand.gray100,
  border: `1px solid ${theme.palette.brand.gray200}`,
  borderRadius: 8,
  fontSize: 13,
  color: theme.palette.brand.gray900,
  outline: "none",
  "&::placeholder": { color: theme.palette.brand.gray500 },
  "&:focus": { borderColor: theme.palette.brand.blueAccent },
}));

const SelectedCount = styled("div")(({ theme }: { theme: Theme }) => ({
  fontSize: 12,
  color: theme.palette.brand.gray500,
  whiteSpace: "nowrap" as const,
}));

const GridWrapper = styled("div")({
  display: "grid",
  gap: 12,
  padding: 16,
  maxHeight: 420,
  overflow: "auto",
});

// Multi-column masonry arrangement (old-flow layout), using redesign 12px gutters.
const MasonryWrap = styled("div")({
  padding: 16,
  maxHeight: 480,
  overflow: "auto",
  "& .rt-masonry-grid": {
    display: "flex",
    width: "auto",
    marginLeft: -12,
  },
  "& .rt-masonry-col": {
    paddingLeft: 12,
    backgroundClip: "padding-box",
  },
  "& .rt-masonry-col > *": {
    marginBottom: 12,
  },
});

const MASONRY_BREAKPOINTS = { default: 4, 1100: 3, 800: 2, 500: 1 };

const GroupCard = styled("div")(({ theme }: { theme: Theme }) => ({
  border: `1px solid ${theme.palette.brand.gray200}`,
  borderRadius: 8,
  overflow: "hidden",
}));

const GroupHeader = styled("div")(({ theme }: { theme: Theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "10px 14px",
  cursor: "pointer",
  background: theme.palette.brand.gray50,
  borderBottom: `1px solid ${theme.palette.brand.gray200}`,
  fontSize: 13,
  fontWeight: 600,
  color: theme.palette.brand.gray900,
  userSelect: "none" as const,
}));

const GroupBody = styled("div")({
  padding: "8px 12px",
});

const GroupSearchInput = styled("input")(({ theme }: { theme: Theme }) => ({
  width: "100%",
  height: 30,
  padding: "0 10px",
  background: theme.palette.brand.white,
  border: `1px solid ${theme.palette.brand.gray200}`,
  borderRadius: 8,
  fontSize: 12,
  color: theme.palette.brand.gray900,
  outline: "none",
  marginBottom: 6,
  boxSizing: "border-box" as const,
  "&::placeholder": { color: theme.palette.brand.gray500 },
  "&:focus": { borderColor: theme.palette.brand.blueAccent },
}));

const RoleRow = styled("div")({
  display: "flex",
  flexDirection: "column" as const,
});

const RoleHeaderRow = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: 2,
  minHeight: 32,
});

const ExpandToggle = styled("button")(({ theme }: { theme: Theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 24,
  height: 24,
  padding: 0,
  border: "none",
  background: "transparent",
  cursor: "pointer",
  color: theme.palette.brand.gray500,
  flexShrink: 0,
  "&:hover": { color: theme.palette.brand.gray900 },
}));

const ExpandSpacer = styled("span")({
  display: "inline-block",
  width: 24,
  height: 24,
  flexShrink: 0,
});

const filterRoleTree = (roles: RoleSet[], term: string): RoleSet[] => {
  if (!term) return roles;
  const lower = term.toLowerCase();
  const walk = (nodes: RoleSet[]): RoleSet[] => {
    const result: RoleSet[] = [];
    for (const node of nodes) {
      const children = node.children ? walk(node.children) : [];
      const selfMatch = node.role.toLowerCase().includes(lower);
      if (selfMatch || children.length > 0) {
        result.push({ ...node, children: children.length ? children : undefined });
      }
    }
    return result;
  };
  return walk(roles);
};

const roleTreeMatches = (roles: RoleSet[], term: string): boolean => {
  if (!term) return true;
  const lower = term.toLowerCase();
  let found = false;
  walkRoleSets(roles, (r) => {
    if (r.role.toLowerCase().includes(lower)) found = true;
  });
  return found;
};

type RoleTreeCheckboxesProps = {
  roles: RoleSet[];
  roleTypeId: string;
  checkBoxes: Record<string, boolean>;
  handleChange: (roleTypeId: string, role: RoleSet) => void;
  depth?: number;
  forceExpand?: boolean;
};

const RoleTreeCheckboxes: FC<RoleTreeCheckboxesProps> = ({
  roles,
  roleTypeId,
  checkBoxes,
  handleChange,
  depth = 0,
  forceExpand = false,
}) => (
  <>
    {roles.map((roleSet) => (
      <RoleTreeNode
        key={roleSet.roleId}
        roleSet={roleSet}
        roleTypeId={roleTypeId}
        checkBoxes={checkBoxes}
        handleChange={handleChange}
        depth={depth}
        forceExpand={forceExpand}
      />
    ))}
  </>
);

type RoleTreeNodeProps = {
  roleSet: RoleSet;
  roleTypeId: string;
  checkBoxes: Record<string, boolean>;
  handleChange: (roleTypeId: string, role: RoleSet) => void;
  depth: number;
  forceExpand: boolean;
};

const RoleTreeNode: FC<RoleTreeNodeProps> = ({ roleSet, roleTypeId, checkBoxes, handleChange, depth, forceExpand }) => {
  const hasChildren = !!roleSet.children?.length;
  const [expanded, setExpanded] = useState(false);
  const open = forceExpand || expanded;
  const key = getRoleKey(roleTypeId, roleSet.roleId);

  return (
    <RoleRow style={{ paddingLeft: depth * 12 }}>
      <RoleHeaderRow>
        {hasChildren ? (
          <ExpandToggle
            type="button"
            aria-label={open ? "Collapse" : "Expand"}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setExpanded((p) => !p);
            }}
          >
            {open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          </ExpandToggle>
        ) : (
          <ExpandSpacer />
        )}
        <CheckBox
          checked={!!checkBoxes[key]}
          name={key}
          label={roleSet.role}
          onChange={() => handleChange(roleTypeId, roleSet)}
        />
      </RoleHeaderRow>
      {hasChildren ? (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <RoleTreeCheckboxes
            roles={roleSet.children!}
            roleTypeId={roleTypeId}
            checkBoxes={checkBoxes}
            handleChange={handleChange}
            depth={depth + 1}
            forceExpand={forceExpand}
          />
        </Collapse>
      ) : null}
    </RoleRow>
  );
};

type RoleTitleGroupCardProps = {
  roleTitleGroup: RoleTitleGroup;
  checkBoxes: Record<string, boolean>;
  globalSearch: string;
  handleChange: (roleTypeId: string, role: RoleSet) => void;
  handleSelectAll: (group: RoleTitleGroup, selectAll: boolean) => void;
};

const RoleTitleGroupCard: FC<RoleTitleGroupCardProps> = ({
  roleTitleGroup,
  checkBoxes,
  globalSearch,
  handleChange,
  handleSelectAll,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const searchTerm = (search || globalSearch).trim();
  const filtered = useMemo(() => {
    return filterRoleTree(roleTitleGroup.roleSet, searchTerm.toLowerCase());
  }, [searchTerm, roleTitleGroup.roleSet]);

  const matchesGlobal =
    !globalSearch ||
    roleTitleGroup.roleType.toLowerCase().includes(globalSearch.toLowerCase()) ||
    roleTreeMatches(roleTitleGroup.roleSet, globalSearch);

  let roleCount = 0;
  walkRoleSets(roleTitleGroup.roleSet, () => {
    roleCount += 1;
  });

  let allSelected = roleCount > 0;
  walkRoleSets(roleTitleGroup.roleSet, (rs) => {
    if (!checkBoxes[getRoleKey(roleTitleGroup.roleTypeId, rs.roleId)]) {
      allSelected = false;
    }
  });

  if (!matchesGlobal) return null;

  return (
    <GroupCard>
      <GroupHeader onClick={() => setOpen((p) => !p)}>
        {open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
        {roleTitleGroup.roleType}
      </GroupHeader>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <GroupBody>
          <FormControl component="fieldset" fullWidth>
            <GroupSearchInput
              placeholder="Search roles..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <FormGroup>
              <CheckBox
                checked={allSelected}
                name={`${roleTitleGroup.roleType}_ALL`}
                label="Select all"
                onChange={() => handleSelectAll(roleTitleGroup, !allSelected)}
              />
              <RoleTreeCheckboxes
                roles={filtered}
                roleTypeId={roleTitleGroup.roleTypeId}
                checkBoxes={checkBoxes}
                handleChange={handleChange}
                forceExpand={!!searchTerm}
              />
            </FormGroup>
          </FormControl>
        </GroupBody>
      </Collapse>
    </GroupCard>
  );
};

type Props = {
  checkBoxes: Record<string, boolean>;
  globalSearch: string;
  selectedCount: number;
  onSearchChange: (value: string) => void;
  onClearAll: () => void;
  handleChange: (roleTypeId: string, role: RoleSet) => void;
  handleSelectAll: (group: RoleTitleGroup, selectAll: boolean) => void;
  /** Arrange groups in a multi-column masonry grid (old-flow layout). */
  masonry?: boolean;
};

/**
 * Controlled picker for hand-selecting individual roles & titles from the full
 * roleTitleList. Shared between the Roles & Titles management modal and the
 * campaign-creation Roles & Titles panel. Callers own the selection state.
 */
const RoleTitleManualPicker: FC<Props> = ({
  checkBoxes,
  globalSearch,
  selectedCount,
  onSearchChange,
  onClearAll,
  handleChange,
  handleSelectAll,
  masonry = false,
}) => {
  const roleTitleList = useSelector((state: ReduxApplicationState) =>
    state.campaignToCreate.roleTitleList.filter((g) => g.roleType !== "Companies"),
  );

  const cards = roleTitleList.map((group) => (
    <RoleTitleGroupCard
      key={group.roleType}
      roleTitleGroup={group}
      checkBoxes={checkBoxes}
      globalSearch={globalSearch}
      handleChange={handleChange}
      handleSelectAll={handleSelectAll}
    />
  ));

  return (
    <PickerRoot>
      <Toolbar>
        <SearchInput
          placeholder="Search groups or roles..."
          value={globalSearch}
          onChange={(e) => onSearchChange(e.target.value)}
        />
        <SelectedCount>{selectedCount.toLocaleString()} selected</SelectedCount>
        <SecondaryButton onClick={onClearAll}>Clear All</SecondaryButton>
      </Toolbar>
      {masonry ? (
        <MasonryWrap>
          <Masonry breakpointCols={MASONRY_BREAKPOINTS} className="rt-masonry-grid" columnClassName="rt-masonry-col">
            {cards}
          </Masonry>
        </MasonryWrap>
      ) : (
        <GridWrapper>{cards}</GridWrapper>
      )}
    </PickerRoot>
  );
};

export default RoleTitleManualPicker;
